const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH = 50;
const ENDPOINT = "/api/app-events";

const PII_KEY_RE = /email|phone|name|address|ssn|dob|birth|password|token|secret|apikey|api_key/i;
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const PHONE_RE = /(\+?\d[\d\s().-]{7,}\d)/;

type ActorType = "admin" | "artist" | "client" | "system" | "anon";

export interface ClientAppEvent {
  eventName: string;
  actorType?: ActorType;
  actorId?: string;
  properties?: Record<string, unknown>;
  occurredAt?: string;
}

function redactProps(input: unknown): unknown {
  if (input == null) return input;
  if (typeof input === "string") {
    if (EMAIL_RE.test(input) || PHONE_RE.test(input)) return "[redacted]";
    return input.length > 500 ? input.slice(0, 500) : input;
  }
  if (typeof input === "number" || typeof input === "boolean") return input;
  if (Array.isArray(input)) return input.slice(0, 20).map(redactProps);
  if (typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      if (PII_KEY_RE.test(k)) continue;
      out[k] = redactProps(v);
    }
    return out;
  }
  return undefined;
}

const queue: ClientAppEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;

function bindListeners() {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  window.addEventListener("pagehide", () => flushNow(true));
  window.addEventListener("beforeunload", () => flushNow(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow(true);
  });
  if (!flushTimer) flushTimer = setInterval(() => flushNow(false), FLUSH_INTERVAL_MS);
}

function flushNow(useBeacon: boolean) {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const payload = JSON.stringify({ events: batch });
  try {
    if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => { /* intentionally silent: best-effort side-effect */ });
  } catch {
    // intentionally silent
    /* swallow */
  }
}

export function trackAppEvent(event: ClientAppEvent) {
  if (!event || !event.eventName) return;
  bindListeners();
  const safeProps = event.properties ? (redactProps(event.properties) as Record<string, unknown>) : undefined;
  queue.push({
    eventName: event.eventName.slice(0, 100),
    actorType: event.actorType,
    actorId: event.actorId ? event.actorId.slice(0, 100) : undefined,
    properties: safeProps,
    occurredAt: event.occurredAt || new Date().toISOString(),
  });
  if (queue.length >= MAX_BATCH) flushNow(false);
}

export function flushAppEvents() {
  flushNow(false);
}
